import { useEffect, useRef, useState } from "react";
import { api, streamAi } from "../lib/api";
import type { AppConfig, MIPrompts } from "../lib/types";
import Markdown from "./Markdown";
import { useI18n } from "../lib/i18n";

type Tab = "summary" | "family" | "mi" | "ask" | "context";

// UAE families: Arabic first, then the largest expatriate language communities.
const LANGUAGES = ["Arabic", "English", "Hindi", "Urdu", "Malayalam", "Tagalog", "Bengali", "Tamil", "Persian"];
const RTL = new Set(["Arabic", "Urdu", "Persian"]);

export default function AiPanel({ patientId, config }: { patientId: string; config: AppConfig | null }) {
  const [tab, setTab] = useState<Tab>("summary");
  const [text, setText] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<string | null>(null);
  const [aiGen, setAiGen] = useState<Record<string, boolean>>({});
  const [err, setErr] = useState<string | null>(null);
  const [lang, setLang] = useState("Arabic");
  const { t } = useI18n();
  const [notes, setNotes] = useState("");
  const [question, setQuestion] = useState("");
  const [mi, setMi] = useState<MIPrompts | null>(null);
  const [ctx, setCtx] = useState<unknown>(null);
  const abort = useRef<AbortController | null>(null);
  const base = `/api/patients/${encodeURIComponent(patientId)}/ai`;

  async function run(key: string, url: string, body: unknown) {
    abort.current?.abort();
    const ac = new AbortController();
    abort.current = ac;
    setBusy(key);
    setErr(null);
    setText((t) => ({ ...t, [key]: "" }));
    try {
      const { aiGenerated } = await streamAi(url, body, (s) => setText((t) => ({ ...t, [key]: s })), ac.signal);
      setAiGen((g) => ({ ...g, [key]: aiGenerated }));
    } catch (e) {
      if (!(e instanceof DOMException && e.name === "AbortError")) setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy((b) => (b === key ? null : b));
    }
  }

  useEffect(() => {
    run("summary", `${base}/summary`, {});
    return () => abort.current?.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId]);

  useEffect(() => {
    if (tab === "mi" && !mi) api.mi(patientId).then(setMi).catch((e: Error) => setErr(e.message));
    if (tab === "context" && !ctx) api.aiContext(patientId).then(setCtx).catch((e: Error) => setErr(e.message));
  }, [tab, mi, ctx, patientId]);

  const label = (key: string) =>
    text[key] && !busy ? (
      <div className="ai-label">
        {aiGen[key] ? `AI-generated (${config?.ai.model ?? "Claude"}) - review before use` : "Template (AI off)"}
        <button className="btn small" onClick={() => navigator.clipboard?.writeText(text[key])}>
          Copy
        </button>
      </div>
    ) : null;

  return (
    <section className="card">
      <h2>{t("aiAssistant")}</h2>
      <div className="tabs" role="tablist">
        {(
          [
            ["summary", t("tabBrief")],
            ["family", t("tabFamily")],
            ["mi", t("tabMi")],
            ["ask", t("tabAsk")],
            ["context", t("tabContext")],
          ] as [Tab, string][]
        ).map(([k, l]) => (
          <button key={k} role="tab" aria-selected={tab === k} onClick={() => setTab(k)}>
            {l}
          </button>
        ))}
      </div>

      {tab === "summary" && (
        <>
          <div className="ai-out" aria-live="polite" dir="auto">
            {text.summary ? <Markdown text={text.summary} /> : <span className="muted">Preparing brief…</span>}
          </div>
          {label("summary")}
          <div className="row" style={{ marginTop: 8 }}>
            <button className="btn small" disabled={busy === "summary"} onClick={() => run("summary", `${base}/summary`, {})}>
              {t("regenerate")}
            </button>
          </div>
        </>
      )}

      {tab === "family" && (
        <>
          <div className="row" style={{ marginBottom: 8 }}>
            <label className="field">
              {t("planLanguage")}
              <select value={lang} onChange={(e) => setLang(e.target.value)}>
                {LANGUAGES.map((l) => (
                  <option key={l}>{l}</option>
                ))}
              </select>
            </label>
          </div>
          <label className="field" style={{ marginBottom: 8 }}>
            {t("familyContext")}
            <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g. loves soccer, both parents work evenings, vegetarian" />
          </label>
          <button className="btn primary" disabled={busy === "family"}
            onClick={() => run("family", `${base}/family-plan`, { language: lang, reading_level: "6th grade", family_notes: notes })}>
            {busy === "family" ? t("drafting") : t("draftPlan")}
          </button>
          {text.family && (
            <div className="ai-out" style={{ marginTop: 10 }} aria-live="polite" dir={RTL.has(lang) ? "rtl" : "ltr"}
              lang={lang === "Arabic" ? "ar" : undefined}>
              <Markdown text={text.family} />
            </div>
          )}
          {label("family")}
        </>
      )}

      {tab === "mi" && (
        <div className="ai-out">
          {!mi ? (
            <span className="muted">Loading…</span>
          ) : (
            <>
              <h4>Open with</h4>
              <ul>{mi.opening_questions.map((q) => <li key={q}>{q}</li>)}</ul>
              <h4>Rulers</h4>
              <ul>
                <li>{mi.importance_ruler}</li>
                <li>{mi.confidence_ruler}</li>
              </ul>
              <h4>Reflections</h4>
              <ul>{mi.reflections.map((q) => <li key={q}>{q}</li>)}</ul>
              <h4>Listen for change talk</h4>
              <ul>{mi.change_talk_cues.map((q) => <li key={q}>{q}</li>)}</ul>
              <h4>Avoid</h4>
              <ul>{mi.avoid.map((q) => <li key={q}>{q}</li>)}</ul>
              <div className="ai-label">
                {mi.ai_generated ? "AI-generated - tailored to this patient" : "Template (AI off)"} · {mi.kas.join(", ")}
              </div>
            </>
          )}
        </div>
      )}

      {tab === "ask" && (
        <>
          <form
            className="row"
            onSubmit={(e) => {
              e.preventDefault();
              if (question.trim().length >= 3) run("ask", `${base}/ask`, { question });
            }}
          >
            <input type="text" style={{ flex: 1 }} value={question} onChange={(e) => setQuestion(e.target.value)}
              placeholder="e.g. Is she eligible for pharmacotherapy, and what should I discuss?" aria-label="Question" />
            <button className="btn primary" disabled={busy === "ask" || question.trim().length < 3}>
              {t("ask")}
            </button>
          </form>
          {text.ask && (
            <div className="ai-out" style={{ marginTop: 10 }} aria-live="polite">
              <Markdown text={text.ask} />
            </div>
          )}
          {label("ask")}
        </>
      )}

      {tab === "context" && (
        <>
          <p className="small muted">
            Exactly what would be sent to the AI: de-identified engine output. No name, Emirates ID, MRN, date of birth or
            record ids; dates are relative. In the UAE edition the AI stays off until cross-border processing is approved
            (Federal Law No. 2 of 2019) or an in-country model endpoint is configured.
          </p>
          <pre className="code">{ctx ? JSON.stringify(ctx, null, 2) : "Loading…"}</pre>
        </>
      )}
      {err && <p className="error small">{err}</p>}
    </section>
  );
}
